import http from "../../../services/httpService";

const BASE = "/camera-model";

function unwrap(response) {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

export function getStoreTree(storeId) {
  return http.get(`${BASE}/stores/${storeId}/tree/`).then(unwrap);
}

export function getRecorderBrands() {
  return http.get(`${BASE}/recorder-brands/`).then(unwrap);
}

export function getCaptureDevice(publicId) {
  return http.get(`${BASE}/capture-devices/${publicId}/`).then((r) => r?.data ?? null);
}

export function getCamera(publicId) {
  return http.get(`${BASE}/cameras/${publicId}/`).then((r) => r?.data ?? null);
}

/**
 * List the cameras whose alert routing is in simulation mode.
 *
 * Returns the page envelope rather than going through `unwrap`, which drops
 * `count` — the caller needs it to size its pagination.
 *
 * @param {number} page 1-based page number.
 * @param {number} pageSize Rows per page.
 * @returns {Promise<{results: object[], count: number}>} The page and its total.
 */
export function getCamerasInSimulation(page = 1, pageSize = 20) {
  return http.get(`${BASE}/cameras/in-simulation/?page=${page}&page_size=${pageSize}`).then((r) => ({
    results: r?.data?.results ?? [],
    count: r?.data?.count ?? 0,
  }));
}

export function getView(publicId) {
  return http.get(`${BASE}/views/${publicId}/`).then((r) => r?.data ?? null);
}

export function postChangeset(payload) {
  return http.post(`${BASE}/changeset/`, payload).then((r) => r?.data ?? null);
}

/**
 * Start an asynchronous screenshot operation for up to 64 cameras of a store.
 *
 * Contract: manager-api `docs/api/screenshot-operations-v1.md`. Re-sending the same key with the
 * same body returns the operation already created instead of a new one, which is what makes a
 * retry after a 429 or a network error safe.
 *
 * @param {number|string} storeId - The store id.
 * @param {string[]} cameraPublicIds - The cameras to capture (`cam_*`), at most 64.
 * @param {string} idempotencyKey - One key per batch, reused on every retry of that batch.
 * @param {string} [mode] - `refresh` recaptures; `missing_only` skips cameras already stored.
 * @returns {Promise<object|null>} The operation: `{ operation_id, status, poll_after_ms, items }`.
 */
export function startScreenshotOperation(storeId, cameraPublicIds, idempotencyKey, mode = "refresh") {
  return http
    .post(
      `${BASE}/stores/${storeId}/screenshot-operations/`,
      { camera_public_ids: cameraPublicIds, mode },
      { headers: { "Idempotency-Key": idempotencyKey } }
    )
    .then((r) => r?.data ?? null);
}

/**
 * Read the progress of a screenshot operation.
 *
 * @param {number|string} storeId - The store id.
 * @param {string} operationId - The `operation_id` returned by {@link startScreenshotOperation}.
 * @returns {Promise<object|null>} The operation, same shape as the start response.
 */
export function fetchScreenshotOperation(storeId, operationId) {
  return http.get(`${BASE}/stores/${storeId}/screenshot-operations/${operationId}/`).then((r) => r?.data ?? null);
}

/**
 * Read the screenshots already stored in S3 for some cameras of a store. Never captures.
 *
 * @param {number|string} storeId - The store id.
 * @param {string[]} cameraPublicIds - The cameras to read, at most 64 (one catalogue page).
 * @returns {Promise<object[]>} One entry per camera: `{ camera_public_id, complete_field, views }`,
 *   each image being `{ url, last_modified, size, revision }` or null. URLs expire after 60 s.
 */
export function fetchScreenshotCatalog(storeId, cameraPublicIds) {
  return http
    .get(`${BASE}/stores/${storeId}/screenshots/`, { params: { camera_public_ids: cameraPublicIds.join(",") } })
    .then((r) => r?.data?.cameras ?? []);
}
